// AdminPanel.js — Punto de entrada de /admin-panel.
//
// 1) Interruptor de seguridad entre el shell nuevo (AdminLayout, sidebar tipo
//    WordPress) y el panel clásico (AdminDashboard, barras horizontales):
//
//      /admin-panel?ui=clasico  → panel anterior, y queda recordado
//      /admin-panel?ui=nuevo    → panel nuevo, y queda recordado
//
//    La preferencia se guarda en localStorage, así que si algo fallara en
//    producción un administrador puede volver al panel de siempre al instante,
//    sin necesidad de desplegar nada.
//
// 2) Ambos shells se cargan de forma diferida (React.lazy). Así todo el código
//    de administración sale del bundle principal: los estudiantes dejan de
//    descargar un panel que nunca van a abrir.
import React, { Suspense, lazy, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { ADMIN_COLORS, AdminSkeletonLine, AdminSkeletonCard } from '../components/admin/AdminShared';

const AdminDashboardClasico = lazy(() => import('./AdminDashboard'));
const AdminLayout = lazy(() => import('./AdminLayout'));

const LS_UI = 'admin_ui';

const leerPreferencia = () => {
  try { return localStorage.getItem(LS_UI); } catch (e) { return null; }
};

// ============================================================
// ESQUELETO DE CARGA
// ============================================================

const CargaWrap = styled.div`
  min-height: 100vh;
  background: ${ADMIN_COLORS.bg};
  display: flex;
`;

const CargaSidebar = styled.div`
  width: 250px;
  flex-shrink: 0;
  background: #111827;

  @media (max-width: 1024px) { display: none; }
`;

const CargaMain = styled.div`
  flex: 1;
  padding: 24px;
  min-width: 0;
`;

const Cargando = () => (
  <CargaWrap>
    <CargaSidebar />
    <CargaMain>
      <AdminSkeletonLine $w="240px" $h={22} $mb={24} />
      <AdminSkeletonCard>
        <AdminSkeletonLine $w="60%" />
        <AdminSkeletonLine $w="90%" />
        <AdminSkeletonLine $w="75%" $mb={0} />
      </AdminSkeletonCard>
      <AdminSkeletonCard>
        <AdminSkeletonLine $w="45%" />
        <AdminSkeletonLine $w="80%" $mb={0} />
      </AdminSkeletonCard>
    </CargaMain>
  </CargaWrap>
);

// ============================================================
// COMPONENTE
// ============================================================

const AdminPanel = () => {
  const { search } = useLocation();
  const param = new URLSearchParams(search).get('ui');
  const valido = param === 'clasico' || param === 'nuevo' ? param : null;
  const modo = valido || leerPreferencia() || 'nuevo';

  useEffect(() => {
    if (!valido) return;
    try { localStorage.setItem(LS_UI, valido); } catch (e) { /* modo privado */ }
  }, [valido]);

  return (
    <Suspense fallback={<Cargando />}>
      {modo === 'clasico' ? <AdminDashboardClasico /> : <AdminLayout />}
    </Suspense>
  );
};

export default AdminPanel;
